import { useCallback } from "react";
import { FilterState } from "../SparcWalkTypes";
import { DropdownItem } from "../../../types/sparcWalk";
import { isIOSMobile } from "../utils/deviceDetection";

interface UseSparcWalkFilterHandlersProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  appliedFilters: FilterState;
  setAppliedFilters: (filters: FilterState) => void;
  applyFilters: (filters?: FilterState) => Promise<void>;
  applyFiltersForIOSMobile: (filters?: FilterState) => void;
  setSparcWalkData: (data: any[]) => void;
  fullSparcWalkData: any[];
}

/**
 * Custom hook to handle filter change events and reset operations
 * Manages both web and mobile filter interactions
 */
const useSparcWalkFilterHandlers = ({
  filters,
  setFilters,
  appliedFilters,
  setAppliedFilters,
  applyFilters,
  applyFiltersForIOSMobile,
  setSparcWalkData,
  fullSparcWalkData,
}: UseSparcWalkFilterHandlersProps) => {
  // Handle web filter changes (immediate API call)
  const handleWebFilterChange = useCallback(
    (key: keyof FilterState, value: string | DropdownItem | null) => {
      console.log("Web filter change:", key, value);

      let normalizedValue: any = value;

      // Normalize dropdown object values
      if (value && typeof value === "object") {
        // For email fields (Created_By, Modified_By), extract email
        if ((key === "Created_By" || key === "Modified_By") && "email" in value) {
          normalizedValue = (value as any).email || "";
        }
        // For zone and category, keep the object (they need the full DropdownItem)
        else if (key === "zone" || key === "category") {
          normalizedValue = value;
        }
        // For all other fields (status, period, week, year), extract displayText or id
        else if ("displayText" in value) {
          normalizedValue = (value as any).displayText === "All" ? "" : (value as any).displayText;
        } else if ("id" in value) {
          normalizedValue = (value as any).id === "" ? "" : (value as any).id;
        }
      }

      const updatedFilters = {
        ...filters,
        [key]: normalizedValue,
      };

      setFilters(updatedFilters);

      // For web, apply filters immediately
      applyFilters(updatedFilters);
    },
    [filters, setFilters, applyFilters]
  );

  // Handle mobile filter changes (updates filters, applies on button press)
  const handleMobileFilterChange = useCallback(
    (key: keyof FilterState, value: string | DropdownItem | null) => {
      console.log("Mobile filter change:", key, value);

      let normalizedValue: any = value;

      // Normalize dropdown object values
      if (value && typeof value === "object") {
        // For email fields (Created_By, Modified_By), extract email
        if ((key === "Created_By" || key === "Modified_By") && "email" in value) {
          normalizedValue = (value as any).email || "";
        }
        // For zone and category, keep the object (they need the full DropdownItem)
        else if (key === "zone" || key === "category") {
          normalizedValue = value;
        }
        // For all other fields (status, period, week, year), extract displayText or id
        else if ("displayText" in value) {
          normalizedValue = (value as any).displayText === "All" ? "" : (value as any).displayText;
        } else if ("id" in value) {
          normalizedValue = (value as any).id === "" ? "" : (value as any).id;
        }
      }

      const updatedFilters = {
        ...filters,
        [key]: normalizedValue,
      };

      setFilters(updatedFilters);

      // For iOS mobile, update filter state but don't apply yet
      // User will tap "Apply" button to trigger applyFiltersForIOSMobile
    },
    [filters, setFilters]
  );

  // Reset filters to default state
  const resetFilters = useCallback(async () => {
    console.log("Resetting filters");

    const defaultFilters: FilterState = {
      zone: null,
      category: null,
      Created_By: "",
      Modified_By: "",
      action: "",
      period: "",
      week: "",
      year: "",
    };

    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);

    // For iOS mobile, reset to full data
    if (isIOSMobile()) {
      console.log("🔄 iOS Mobile reset: restoring full dataset");
      setSparcWalkData([...fullSparcWalkData]);
    } else {
      // For web/iPad, trigger API call with no filters
      await applyFilters(defaultFilters);
    }
  }, [
    setFilters,
    setAppliedFilters,
    setSparcWalkData,
    fullSparcWalkData,
    applyFilters,
  ]);

  return {
    handleWebFilterChange,
    handleMobileFilterChange,
    resetFilters,
  };
};

export default useSparcWalkFilterHandlers;
